/**
 * File: useIntersectionObserver.js
 * Path: /src/hooks/useIntersectionObserver.js
 * Description: Custom hook for detecting when elements enter the viewport.
 * Key Features: Configurable threshold, root margin, and single/continuous observation options.
 * Maintenance Notes: Foundational for scroll animations throughout the site.
 */

import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to observe when an element intersects with the viewport
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - A number between 0 and 1 indicating the percentage that should be visible
 * @param {string|Element} options.root - The element that is used as the viewport for checking visibility
 * @param {string} options.rootMargin - Margin around the root
 * @param {boolean} options.triggerOnce - Whether to observe once or continuously
 * @returns {Array} [ref, isIntersecting, entry] - ref to attach to element, boolean for visibility, full IntersectionObserverEntry
 */
const useIntersectionObserver = ({
  threshold = 0.1,
  root = null,
  rootMargin = '0px',
  triggerOnce = true
} = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [entry, setEntry] = useState(null);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // Create a new observer instance
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        setEntry(entry);
        
        // If triggerOnce is true and the element is visible, unobserve it
        if (triggerOnce && entry.isIntersecting) {
          observerRef.current.unobserve(elementRef.current);
        }
      },
      { threshold, root, rootMargin }
    );

    const currentElement = elementRef.current;
    const currentObserver = observerRef.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [threshold, root, rootMargin, triggerOnce]);

  return [elementRef, isIntersecting, entry];
};

export default useIntersectionObserver;